import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Shield, Info } from "lucide-react";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from "../components/ui/breadcrumb";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <Link to="/" className="text-blue-600 hover:underline text-sm">← Back to WANTTD</Link>
          </div>
          
          <Breadcrumb className="mb-4">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="#">Legal</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink>Privacy Policy</BreadcrumbLink>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          
          <Card className="shadow-sm border border-gray-200">
            <CardHeader className="pb-2 border-b">
              <div className="flex items-center gap-3 mb-2">
                <Shield className="h-6 w-6 text-gray-700" />
                <CardTitle className="text-2xl font-bold text-gray-800">Privacy Policy</CardTitle>
              </div>
              <p className="text-sm text-gray-500">Last Updated: April 2025</p>
            </CardHeader>
            
            <CardContent className="pt-6">
              <div className="space-y-4 text-gray-600">
                <p>
                  WANTTD and its affiliated companies are committed to protecting the personal information of its users. 
                  This Privacy Policy ("Policy") explains how WANTTD Ltd. ("WANTTD," "we," "us") processes personal data in connection with our platform and services.
                </p>
                <p>
                  By accessing or using www.wanttd.com, the WANTTD mobile app, or any related platforms operated by WANTTD (each, the "Site"), 
                  you acknowledge that you have read and understood this Policy. This Policy may change from time to time, and we will post updates accordingly. 
                  We recommend that you check the Policy periodically for updates.
                </p>
              </div>
              
              <Tabs defaultValue="collect" className="w-full mt-6">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="collect">Information</TabsTrigger>
                  <TabsTrigger value="use">Usage & Legal</TabsTrigger>
                  <TabsTrigger value="rights">Rights & Contact</TabsTrigger>
                </TabsList>
                
                <TabsContent value="collect" className="mt-4 space-y-5">
                  <h3 className="font-medium text-lg">1. Information We Collect</h3>
                  <p className="text-gray-600">We collect personal information in the following ways:</p>
                  
                  <div className="bg-white p-4 rounded border border-gray-200">
                    <h4 className="font-medium mb-2">Information You Provide:</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-600 text-sm">
                      <li>When you register for an account, post a desire, make an offer, or engage in transactions.</li>
                      <li>When you communicate with WANTTD or other users.</li>
                      <li>When you participate in surveys, events, or marketing campaigns.</li>
                      <li>When you upload content, including text, images, videos, or any other media.</li>
                      <li>When you provide payment information for transactions.</li>
                      <li>When you submit customer support inquiries or report issues.</li>
                    </ul>
                  </div>
                  
                  <div className="bg-white p-4 rounded border border-gray-200">
                    <h4 className="font-medium mb-2">Information We Collect Automatically:</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-600 text-sm">
                      <li>Usage data (browsing activity, IP address, device info)</li>
                      <li>Cookies and tracking technologies</li>
                      <li>Interaction logs, timestamps, and preferences</li>
                    </ul>
                  </div>
                  
                  <div className="bg-white p-4 rounded border border-gray-200">
                    <h4 className="font-medium mb-2">Information from Third Parties:</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-600 text-sm">
                      <li>Service providers (fraud prevention, security)</li>
                      <li>Linked third-party accounts (Google, Facebook)</li>
                      <li>Publicly available sources for profile enhancement</li>
                      <li>Payment providers for transaction validation</li>
                    </ul>
                  </div>
                </TabsContent>
                
                <TabsContent value="use" className="mt-4 space-y-5">
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-medium text-lg mb-3">2. Legal Basis for Processing</h3>
                      <p className="text-gray-600">We process personal information based on the following legal grounds:</p>
                      <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-600">
                        <li>Consent – When you provide consent for specific uses.</li>
                        <li>Contractual Necessity – To fulfill our obligations to you (e.g., providing services, processing payments).</li>
                        <li>Legal Compliance – To meet regulatory requirements and comply with laws.</li>
                        <li>Legitimate Interests – To improve our services, prevent fraud, enhance security, and optimize user experience.</li>
                      </ul>
                    </div>
                    
                    <div className="bg-white p-4 rounded border border-gray-200">
                      <h4 className="font-medium mb-2">We use your data to:</h4>
                      <ul className="list-disc pl-5 space-y-1 text-gray-600 text-sm">
                        <li>Operate and improve our services</li>
                        <li>Facilitate transactions between users</li>
                        <li>Ensure security and prevent fraud</li>
                        <li>Provide support and resolve disputes</li>
                        <li>Send promotional content (opt-out available)</li>
                        <li>Conduct research and business intelligence</li>
                        <li>Comply with legal obligations</li>
                      </ul>
                    </div>
                    
                    <div className="bg-white p-4 rounded border border-gray-200">
                      <h4 className="font-medium mb-2">Data Sharing:</h4>
                      <p className="text-gray-600 text-sm">We share data with other users for service fulfillment, third-party service providers, business partners, law enforcement when required, and corporate affiliates.</p>
                      <p className="text-gray-600 text-sm mt-2">We do not sell personal data for monetary compensation.</p>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="rights" className="mt-4 space-y-5">
                  <h3 className="font-medium text-lg">Your Privacy Rights</h3>
                  <div className="bg-white p-4 rounded border border-gray-200">
                    <p className="text-gray-600 text-sm mb-3">Depending on your jurisdiction, you may have rights to:</p>
                    <ul className="list-disc pl-5 space-y-1 text-gray-600 text-sm">
                      <li>Access, correct, or delete your personal information</li>
                      <li>Object to processing or restrict data use</li>
                      <li>Withdraw consent for marketing communications</li>
                      <li>Receive your data in a structured format</li>
                      <li>Lodge complaints with data protection authorities</li>
                    </ul>
                    <p className="mt-3 text-gray-600 text-sm">
                      To exercise your rights, please contact us at <span className="text-blue-600">privacy@wanttd.com</span>
                    </p>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
